// components/notifications/NotificationItem.js
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

/**
 * NotificationItem component
 * Renders either a toast notification or an entry in notification dropdown
 *
 * @param {Object} props - Component props
 * @param {Object} props.notification - Notification data object
 * @param {Function} props.onClose - Function to close the notification
 * @param {Function} props.onDismiss - Function to dismiss the notification
 * @param {Function} props.onRead - Function to mark notification as read
 * @param {boolean} props.isInDropdown - Whether this item is in dropdown (true) or toast (false)
 */
const NotificationItem = ({
  notification = {},
  onClose,
  onDismiss,
  onRead,
  isInDropdown = false,
}) => {
  const navigate = useNavigate();
  const [isOldNotification, setIsOldNotification] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [dismissHovered, setDismissHovered] = useState(false);
  const [itemHovered, setItemHovered] = useState(false);

  // Safety defaults for notification properties
  const {
    id = "",
    title = "",
    message = "",
    type = "info",
    link = null,
    linkText = null,
    status = "active",
    createdAt = new Date(),
    userAvatar = null,
    userName = null,
  } = notification || {};

  // Determine if notification is old on component mount
  useEffect(() => {
    const checkIfOld = () => {
      if (!notification || typeof notification !== "object") {
        return false;
      }

      // Dismissed notifications are considered old
      if (status === "dismissed") {
        return true;
      }

      // Check if notification is older than a week
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      let notificationDate;

      if (createdAt instanceof Date) {
        notificationDate = createdAt;
      } else if (createdAt && typeof createdAt.toDate === "function") {
        notificationDate = createdAt.toDate();
      } else if (createdAt) {
        notificationDate = new Date(createdAt);
      } else {
        return false;
      }

      return notificationDate < oneWeekAgo;
    };

    setIsOldNotification(checkIfOld());
  }, [id, status, createdAt, notification]);

  // Auto-dismiss after 10 seconds for active toast
  useEffect(() => {
    // Only for active toast notifications
    if (status === "active" && !isInDropdown && onClose) {
      // 10 second timer for auto-dismiss of toast notifications
      const timer = setTimeout(() => {
        // Auto-timeout - NOT manually dismissed, change status to "new"
        onClose({ id, markAsRead: false });
      }, 10000); // 10 seconds

      return () => {
        clearTimeout(timer);
      };
    }
  }, [status, isInDropdown, onClose, id]);

  // Early return AFTER all hooks have been called
  if (!notification || typeof notification !== "object") {
    return null;
  }

  // Get styles based on notification type (success, error, warning, info)
  const getTypeStyles = () => {
    switch (type) {
      case "success":
        return {
          color: "#4ade80", // Light green
          bgColor: "#f0fdf4", // Very light green bg
          iconBg: "#22c55e", // Emerald green for icon
          barColor: "bg-green-500",
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          ),
        };
      case "error":
        return {
          color: "#f87171", // Light red
          bgColor: "#fef2f2", // Very light red bg
          iconBg: "#ef4444", // Red for icon
          barColor: "bg-red-500",
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ),
        };
      case "warning":
        return {
          color: "#facc15", // Light amber
          bgColor: "#fefce8", // Very light amber bg
          iconBg: "#eab308", // Amber for icon
          barColor: "bg-amber-500",
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
          ),
        };
      default: // info
        return {
          color: "#60a5fa", // Light blue
          bgColor: "#eff6ff", // Very light blue bg
          iconBg: "#3b82f6", // Blue for icon
          barColor: "bg-blue-500",
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12" y2="8"></line>
            </svg>
          ),
        };
    }
  };

  const styles = getTypeStyles();

  // Handle click on the link (if present)
  const handleGoToLink = () => {
    if (link && onClose) {
      if (link.startsWith("http")) {
        window.open(link, "_blank");
      } else {
        navigate(link);
      }
      if (!isInDropdown) {
        // Close the notification and mark as read
        onClose({ id, markAsRead: true });
      }
    }
  };

  // Handle dismiss button click
  const handleDismiss = () => {
    if (onDismiss) {
      onDismiss(id);

      // Provide immediate visual feedback in dropdown
      if (isInDropdown) {
        const container = document.getElementById(`notification-${id}`);
        if (container) {
          container.style.height = "0";
          container.style.opacity = "0";
          container.style.marginBottom = "0";
          container.style.overflow = "hidden";
          container.style.transition = "all 0.3s ease-out";
        }
      }
    }
  };

  // Handle close button click (OK button on toast)
  const handleClose = () => {
    if (onClose) {
      // Close the notification and mark as read
      onClose({ id, markAsRead: true });
    }
  };

  // Handle marking notification as read
  const handleMarkAsRead = () => {
    if (onRead) {
      onRead(id);
    }
  };

  // Determine if title and text need truncation
  const needsTitleTruncation = title.length > 30;
  const needsTextTruncation = message.length > 65;

  // For text truncation with visible "...more" text in second line
  const truncatedText = () => {
    if (isExpanded) {
      return message;
    }

    if (needsTextTruncation) {
      return (
        <>
          {message.substring(0, 61)}
          <span className="text-xs text-brandPrimary mt-1"> ...</span>
        </>
      );
    }

    return message;
  };

  // For title truncation
  const truncatedTitle = () => {
    if (isExpanded) {
      return title;
    }

    if (needsTitleTruncation) {
      return (
        <>
          {title.substring(0, 30)}
          <span className="text-xs text-brandPrimary"> ...</span>
        </>
      );
    }

    return title;
  };

  // Text expand/collapse animation variants
  const textVariants = {
    collapsed: { height: "2.5rem", overflow: "hidden" },
    expanded: { height: "auto", overflow: "visible" },
  };

  // For notifications in the dropdown (notification center)
  if (isInDropdown) {
    // Determine if notification is "new" (unread)
    const isNew = status === "new";

    // Set opacity based on state
    const opacityValue =
      status === "read" || status === "dismissed" || isOldNotification
        ? 0.7 // Lower opacity for read/dismissed/old
        : 1.0; // Full opacity for new/active

    // Handler for clicking on the notification
    const handleNotificationClick = () => {
      // Only mark as read if it's currently new
      if (isNew && onRead) {
        handleMarkAsRead();
      }
    };

    return (
      <motion.div
        id={`notification-${id}`}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ type: "spring", damping: 20, stiffness: 300 }}
        style={{ opacity: opacityValue }}
        className={`flex w-full border-b border-gray-200 select-none relative${
          isNew ? " cursor-pointer" : ""
        }`}
        onMouseEnter={() => {
          setItemHovered(true);
          // Auto-expand text on hover instead of click
          if (needsTextTruncation || needsTitleTruncation) {
            setIsExpanded(true);
          }
        }}
        onMouseLeave={() => {
          setItemHovered(false);
          // Collapse text when hover ends
          if (needsTextTruncation || needsTitleTruncation) {
            setIsExpanded(false);
          }
        }}
        onClick={handleNotificationClick}
      >
        {/* New Notification Indicator - LEFT */}
        {isNew && (
          <div className="absolute left-0 top-0 z-20">
            <div
              className="w-3 h-3 rounded-full ml-2 mt-2"
              style={{ backgroundColor: styles.iconBg }}
            >
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.7, 1, 0.7],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "loop",
                }}
                className="w-full h-full rounded-full"
                style={{ backgroundColor: styles.iconBg }}
              />
            </div>
          </div>
        )}

        {/* Base background with proper transparency */}
        <div
          className={`absolute inset-0 ${
            isNew ? styles.barColor : "bg-gray-100"
          }`}
          style={{
            opacity: isNew
              ? itemHovered
                ? 0.25
                : 0.12
              : itemHovered
              ? 0.15
              : 0.05,
            transition: "opacity 180ms cubic-bezier(0.25, 0.1, 0.25, 1.0)",
            zIndex: 0,
          }}
        ></div>

        {/* Card surface for hover effects - always present but transformed */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{
            transition: "all 160ms cubic-bezier(0.2, 0, 0.1, 1)",
            zIndex: 0,
            transform: itemHovered ? "scale(1.02)" : "scale(1)",
            opacity: itemHovered ? 1 : 0,
            boxShadow: itemHovered
              ? "0 4px 24px 0 rgba(0, 0, 0, 0.05), 0 8px 24px 0 rgba(0, 0, 0, 0.15)"
              : "none",
          }}
        >
          {/* Color gradient */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to bottom right, ${styles.iconBg}30, ${styles.iconBg}50)`,
              opacity: 0.5,
              zIndex: 1,
              transition: "opacity 200ms cubic-bezier(0.2, 0, 0.1, 1)",
            }}
          ></div>

          {/* White shine effect from top-left */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.2) 60%, rgba(255,255,255,0) 80%)`,
              zIndex: 2,
              transition: "opacity 150ms cubic-bezier(0.2, 0, 0.1, 1)",
              opacity: itemHovered ? 1 : 0,
            }}
          ></div>
        </div>

        {/* Left colored border with animation for new notifications */}
        <div
          className={`w-1 self-stretch flex-shrink-0 z-10 overflow-hidden`}
          style={{ backgroundColor: styles.iconBg }}
        >
          {isNew && (
            <motion.div
              initial={{ y: "100%" }}
              animate={{
                y: ["-100%", "100%"],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "loop",
              }}
              className="w-full h-full"
              style={{
                backgroundImage: `linear-gradient(to bottom, transparent, ${styles.iconBg}, transparent)`,
                opacity: 0.8,
              }}
            />
          )}
        </div>

        {/* Left icon section */}
        <div className="px-4 py-3 flex items-center z-10">
          <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center">
            {userAvatar ? (
              <img
                src={userAvatar}
                alt="User"
                className="w-full h-full object-cover"
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{ backgroundColor: styles.iconBg }}
              >
                {styles.icon}
              </div>
            )}
          </div>
        </div>

        {/* Middle content section */}
        <div className={`flex-1 py-3 pr-2 flex flex-col justify-center z-10`}>
          {userName ? (
            <p className="text-sm font-semibold" style={{ color: "#3b82f6" }}>
              {userName}
            </p>
          ) : (
            <p
              className="text-sm font-semibold"
              style={{ color: styles.iconBg }}
            >
              {truncatedTitle()}
            </p>
          )}

          <div className="relative overflow-hidden">
            <AnimatePresence initial={false}>
              <motion.div
                className="text-sm text-gray-700 overflow-hidden"
                variants={textVariants}
                initial={needsTextTruncation ? "collapsed" : "expanded"}
                animate={
                  isExpanded || !needsTextTruncation ? "expanded" : "collapsed"
                }
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {truncatedText()}
              </motion.div>
            </AnimatePresence>
          </div>

          <p className="text-xs text-gray-400 mt-1">
            {isNew && (
              <span
                className="mr-2 font-semibold"
                style={{ color: styles.iconBg }}
              >
                NEW
              </span>
            )}
            {formatTimeAgo(createdAt)}
          </p>
        </div>

        {/* Right action section with buttons */}
        <div className="flex flex-col min-w-[80px] z-10">
          {/* Show Visit button if link exists */}
          {link && (
            <div
              className="h-1/2"
              style={{
                borderLeft: `1px solid ${
                  itemHovered ? `${styles.iconBg}33` : "#e5e7eb"
                }`,
                transition: "border-color 160ms cubic-bezier(0.2, 0, 0.1, 1)",
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the parent's onClick
                  handleGoToLink();
                  // Also mark as read when clicking Visit
                  if (isNew && onRead) {
                    handleMarkAsRead();
                  }
                }}
                className="w-full h-full px-4 py-3 text-sm text-brandPrimary font-medium hover:bg-brandPrimary hover:text-white flex items-center justify-center"
                style={{
                  borderBottom: `1px solid ${
                    itemHovered ? `${styles.iconBg}33` : "#e5e7eb"
                  }`,
                  transition: "border-color 160ms cubic-bezier(0.2, 0, 0.1, 1)",
                }}
              >
                Visit
              </button>
            </div>
          )}

          {/* Dismiss Button - only button we keep */}
          <div
            className={`${!link ? "h-full" : "h-1/2"}`}
            style={{
              borderLeft: `1px solid ${
                itemHovered ? `${styles.iconBg}33` : "#e5e7eb"
              }`,
              transition: "border-color 160ms cubic-bezier(0.2, 0, 0.1, 1)",
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering the parent's onClick
                handleDismiss();
              }}
              onMouseEnter={() => setDismissHovered(true)}
              onMouseLeave={() => setDismissHovered(false)}
              className="w-full h-full px-4 py-3 text-sm font-medium flex items-center justify-center transition-colors"
              style={{
                backgroundColor: dismissHovered ? styles.iconBg : "transparent",
                color: dismissHovered ? "#ffffff" : styles.iconBg,
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  // For popup toast notifications (floating notifications)
  const popupOpacity = isOldNotification ? 0.7 : 1;

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 50 }}
      transition={{ type: "spring", damping: 20, stiffness: 300 }}
      style={{ opacity: popupOpacity }}
      className="w-96 overflow-hidden shadow-lg rounded-md select-none relative"
      onMouseEnter={() => {
        // Auto-expand text on hover instead of click
        if (needsTextTruncation || needsTitleTruncation) {
          setIsExpanded(true);
        }
      }}
      onMouseLeave={() => {
        // Collapse text when hover ends
        if (needsTextTruncation || needsTitleTruncation) {
          setIsExpanded(false);
        }
      }}
    >
      <div
        className="flex rounded-md overflow-hidden relative"
        style={{ backgroundColor: styles.bgColor }}
      >
        {/* Background progress animation that covers all but the buttons */}
        <motion.div
          className={`absolute top-0 bottom-0 left-0 z-0 ${styles.barColor}`}
          style={{ opacity: 0.15 }}
          initial={{ width: "calc(100% - 80px)" }} // Exclude the right buttons area
          animate={{ width: "0%" }}
          transition={{ duration: 10, ease: "linear" }}
        />

        {/* Left colored border */}
        <div
          className="w-1.5 flex-shrink-0 self-stretch z-10 relative"
          style={{ backgroundColor: styles.iconBg }}
        ></div>

        {/* Main content area */}
        <div className="flex flex-1 z-10 relative">
          {/* Left icon section */}
          <div className="px-4 py-4 flex items-center">
            <div
              className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center"
              style={{ backgroundColor: styles.iconBg }}
            >
              {styles.icon}
            </div>
          </div>

          {/* Middle content section */}
          <div className="flex-1 py-4 flex flex-col justify-center overflow-hidden">
            <p className="font-medium" style={{ color: styles.iconBg }}>
              {truncatedTitle()}
            </p>

            {message && (
              <div className="relative">
                <AnimatePresence initial={false}>
                  <motion.div
                    className="text-sm text-gray-700 mt-1 overflow-hidden"
                    variants={textVariants}
                    initial={needsTextTruncation ? "collapsed" : "expanded"}
                    animate={
                      isExpanded || !needsTextTruncation
                        ? "expanded"
                        : "collapsed"
                    }
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    {truncatedText()}
                  </motion.div>
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Right action section */}
          <div
            className="relative z-20"
            style={{
              borderLeft: `1px solid #e5e7eb`,
            }}
          >
            {link ? (
              <div className="flex flex-col h-full">
                <div
                  style={{
                    borderBottom: `1px solid #e5e7eb`,
                  }}
                >
                  <HoverableButton
                    onClick={handleGoToLink}
                    text={linkText || "Visit"}
                    color={styles.iconBg}
                  />
                </div>
                <div>
                  <HoverableButton
                    onClick={handleClose}
                    text="OK"
                    color={styles.iconBg}
                  />
                </div>
              </div>
            ) : (
              <HoverableButton
                onClick={handleClose}
                text="OK"
                color={styles.iconBg}
              />
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Helper function to format time ago
 * @param {Date|string|object} date - Date object, timestamp, or Firestore timestamp
 * @returns {string} Human-readable time ago string
 */
const formatTimeAgo = (date) => {
  if (!date) return "Just now";

  try {
    // Convert to Date object if needed
    const dateObj =
      date instanceof Date
        ? date
        : typeof date.toDate === "function"
        ? date.toDate()
        : new Date(date);

    const now = new Date();
    const diffMs = now - dateObj;
    const diffSec = Math.round(diffMs / 1000);
    const diffMin = Math.round(diffSec / 60);
    const diffHour = Math.round(diffMin / 60);
    const diffDay = Math.round(diffHour / 24);

    if (diffSec < 60) return "Just now";
    if (diffMin < 60) return `${diffMin} min${diffMin === 1 ? "" : "s"} ago`;
    if (diffHour < 24)
      return `${diffHour} hour${diffHour === 1 ? "" : "s"} ago`;
    if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;

    return dateObj.toLocaleDateString();
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Unknown time";
  }
};

/**
 * HoverableButton component - Button with hover effect
 */
const HoverableButton = ({ onClick, text, color }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="h-full w-full px-6 py-4 text-sm font-medium flex items-center justify-center transition-colors"
      style={{
        color,
        backgroundColor: hovered ? color + "20" : "transparent",
      }}
    >
      {text}
    </button>
  );
};

export default NotificationItem;
